import { PaymentMethod } from '@lucidtech/las-sdk-browser';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useSearch } from '@tanstack/react-router';
import { useSessionStorage } from '@uidotdev/usehooks';
import { memo, useCallback, useEffect, useState } from 'react';

import { BasicModal, Button, Dialog, Heading, Text } from '@/components/core';
import { AddIcon, SaveIcon } from '@/components/icons';
import { useCreatePaymentMethod, useDeletePaymentMethod, useUpdatePaymentMethod } from '@/hooks/api';

const PaymentMethodModal = () => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    stripe
      .confirmSetup({
        elements,
        confirmParams: {
          return_url: `${import.meta.env.VITE_APP_URL}/settings/billing`,
        },
      })
      .then(console.log);
  };

  return (
    <form onSubmit={handleSubmit} className="min-h-[28rem] w-[30rem] p-6">
      <Heading>Add payment details</Heading>

      <div className="flex flex-col gap-4">
        <PaymentElement />
        <Button isDisabled={!stripe} type="submit" variant="primary">
          <Text>Save</Text>
        </Button>
      </div>
    </form>
  );
};

const IAddPaymentMethod = () => {
  const [paymentMethod, setPaymentMethod] = useSessionStorage<(PaymentMethod & { isUpdated?: boolean }) | undefined>(
    'pendingPaymentMethod',
    undefined
  );
  const [isOpen, setIsOpen] = useState(false);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const { mutateAsync: createPaymentMethod, isPending: isPendingCreatePaymentMethod } = useCreatePaymentMethod();
  const { mutateAsync: updatePaymentMethod } = useUpdatePaymentMethod();
  const { mutateAsync: deletePaymentMethod } = useDeletePaymentMethod();
  const { redirectStatus, setupIntentSecret } = useSearch({ from: '/_auth/_org/settings/billing' });

  const setupPaymentMethod = useCallback(async () => {
    const paymentMethod = await createPaymentMethod({});
    setPaymentMethod(paymentMethod);

    if (!stripePromise && paymentMethod?.stripePublishableKey) {
      setStripePromise(loadStripe(paymentMethod.stripePublishableKey));
    }

    setIsOpen(true);
  }, [createPaymentMethod, setPaymentMethod, stripePromise]);

  useEffect(() => {
    if (!paymentMethod || redirectStatus !== 'succeeded') return;

    if (paymentMethod.stripeSetupIntentSecret === setupIntentSecret) {
      updatePaymentMethod({
        id: { paymentMethodId: paymentMethod.paymentMethodId },
        updates: { stripeSetupIntentSecret: setupIntentSecret },
      });
      setPaymentMethod(undefined);
    }
  }, [paymentMethod, redirectStatus, setPaymentMethod, setupIntentSecret, updatePaymentMethod]);

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        if (paymentMethod && redirectStatus !== 'succeeded') {
          deletePaymentMethod({ paymentMethodId: paymentMethod.paymentMethodId });
          setPaymentMethod(undefined);
        }
      }

      setIsOpen(isOpen);
    },
    [deletePaymentMethod, paymentMethod, redirectStatus, setPaymentMethod]
  );

  return (
    <div>
      <BasicModal isOpen={isOpen} onDismiss={() => onOpenChange(false)}>
        <Dialog>
          {paymentMethod?.stripeSetupIntentSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret: paymentMethod.stripeSetupIntentSecret }}>
              <PaymentMethodModal />
            </Elements>
          )}
        </Dialog>
      </BasicModal>

      <Button isDisabled={isPendingCreatePaymentMethod} variant="secondary" onPress={setupPaymentMethod}>
        {isPendingCreatePaymentMethod ? <SaveIcon /> : <AddIcon />}
        <Text>Add payment method</Text>
      </Button>
    </div>
  );
};

export const AddPaymentMethod = memo(IAddPaymentMethod);
